<script setup lang="ts">
import { onBeforeMount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BetaConfirmationModal from '@/components/beta-dashboard/organisms/BetaConfirmationModal.vue'
import api from '@/services/api'
import modal from '@/services/modal'
import notification from '@/services/notification'
import { useAuthStore } from '@/stores/auth'
import { useSiteStore } from '@/stores/site'
import { betaPanelQuery } from '@/utils/panel-variant'

const route = useRoute()
const router = useRouter()
const siteStore = useSiteStore()
const authStore = useAuthStore()
const page = ref<{
    postId: number
    title: string
    route: string
    target_blank: string | number
    [key: string]: unknown
} | null>(null)
const loading = ref(true)
const saving = ref(false)
const loadError = ref(false)

const load = async () => {
    loading.value = true
    loadError.value = false
    try {
        const response = await api.get(`site/${siteStore.id}/post/${route.params.id}`)
        const { status, content } = await response.json()
        if (status != '1' || !content?.postId) throw new Error('Erro ao buscar página')
        page.value = content
    } catch {
        loadError.value = true
    } finally {
        loading.value = false
    }
}

const goDashboard = () => router.push({ name: 'dashboard', query: betaPanelQuery() })

const back = async () => {
    if (saving.value) return
    const confirmed = await modal.confirmation(
        'Voltar para o dashboard?',
        'Esta ação poderá resultar na perda de dados não salvos.',
        { type: 'danger', textConfirm: 'Sim, voltar', textCancel: 'Não, continuar' }
    )
    if (confirmed) await goDashboard()
}

const save = async () => {
    if (saving.value || !page.value) return
    if (!page.value.title || !page.value.route) {
        notification.simple('Preencha todos os campos', 'error')
        return
    }
    if (!page.value.route.includes('https://') && !page.value.route.includes('http://')) {
        page.value.route = `https://${page.value.route}`
    }
    saving.value = true
    try {
        const response = await api.post(
            `site/${siteStore.id}/post/${page.value.postId}/external/update`,
            JSON.stringify(page.value)
        )
        const { status } = await response.json()
        if (status != '1') throw new Error('Erro ao salvar página')
        notification.simple('Página salva com sucesso', 'success')
        await goDashboard()
    } catch {
        notification.simple('Erro ao salvar página', 'error')
    } finally {
        saving.value = false
    }
}

onBeforeMount(load)
onMounted(() => authStore.checkAccessType())
</script>

<template>
    <BetaConfirmationModal />
    <main class="beta-external-editor">
        <header class="beta-external-editor__topbar">
            <button type="button" :disabled="saving" @click="back">
                <span aria-hidden="true">←</span>
                Voltar para o dashboard
            </button>
            <span>Link externo</span>
        </header>

        <div class="beta-external-editor__content">
            <header class="beta-external-editor__heading">
                <p>Páginas do site</p>
                <h1>Configure seu link externo</h1>
                <span>Defina o nome que aparece no menu e para onde ele leva.</span>
            </header>

            <p v-if="loading" role="status">Carregando link externo…</p>
            <div v-else-if="loadError" class="beta-external-editor__card" role="alert">
                <p>Não foi possível carregar o link externo.</p>
                <button type="button" class="beta-external-editor__save" @click="load">
                    Tentar novamente
                </button>
            </div>

            <form
                v-else-if="page"
                class="beta-external-editor__card"
                novalidate
                @submit.prevent="save"
            >
                <fieldset :disabled="saving">
                    <label for="beta-external-title">
                        <span>Título da página</span>
                        <input id="beta-external-title" v-model="page.title" type="text" required />
                    </label>
                    <label for="beta-external-url">
                        <span>URL da página</span>
                        <input
                            id="beta-external-url"
                            v-model="page.route"
                            type="text"
                            inputmode="url"
                            placeholder="https://"
                            required
                        />
                    </label>
                    <label for="beta-external-target">
                        <span>Abrir em</span>
                        <select id="beta-external-target" v-model="page.target_blank">
                            <option value="0">Mesma aba</option>
                            <option value="1">Nova aba</option>
                        </select>
                    </label>
                </fieldset>
                <footer>
                    <button type="submit" class="beta-external-editor__save" :disabled="saving">
                        {{ saving ? 'Salvando…' : 'Salvar' }}
                    </button>
                </footer>
            </form>
        </div>
    </main>
</template>

<style scoped>
.beta-external-editor {
    min-height: 100vh;
    background: #f6f8fb;
    color: #172132;
}
.beta-external-editor__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid #e3e9f0;
    background: #fff;
    padding: 24px 32px;
    color: #758298;
    font-size: 12px;
}
.beta-external-editor__topbar button {
    display: flex;
    align-items: center;
    gap: 8px;
}
.beta-external-editor__content {
    max-width: 840px;
    margin: 0 auto;
    padding: 40px 24px;
}
.beta-external-editor__heading {
    margin-bottom: 28px;
}
.beta-external-editor__heading p {
    color: var(--main-color);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}
.beta-external-editor__heading h1 {
    margin: 8px 0;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.25;
}
.beta-external-editor__heading > span {
    color: #7c8796;
    font-size: 12px;
}
.beta-external-editor__card {
    border: 1px solid #dfe5ec;
    border-radius: 22px;
    background: #fff;
    padding: 28px;
}
.beta-external-editor fieldset {
    display: grid;
    min-width: 0;
    gap: 24px;
}
.beta-external-editor label {
    display: grid;
    gap: 8px;
    color: #40516a;
    font-size: 12px;
    font-weight: 600;
}
.beta-external-editor input,
.beta-external-editor select {
    width: 100%;
    min-height: 46px;
    border: 1px solid #dce3ed;
    border-radius: 10px;
    background: #fff;
    padding: 11px 14px;
    color: #26374e;
    font-size: 13px;
    font-weight: 400;
}
.beta-external-editor footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 28px;
}
.beta-external-editor__save {
    min-height: 42px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 10px 24px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
}
.beta-external-editor :is(input, select, button):focus-visible {
    outline: 2px solid var(--main-color);
    outline-offset: 3px;
}
.beta-external-editor :disabled {
    cursor: wait;
    opacity: 0.65;
}
@media (max-width: 600px) {
    .beta-external-editor__topbar {
        padding: 20px 16px;
    }
    .beta-external-editor__content {
        padding: 28px 16px;
    }
    .beta-external-editor__card {
        padding: 20px;
    }
    .beta-external-editor__heading h1 {
        font-size: 24px;
    }
}
</style>
